import { ENUM_KITCHEN_ROLE } from '@cokitchen/cokitchen-auth';
import { QUICK_EATS, splitScreenColors, STORAGE_KEYS } from '../../helpers/constants';
import { ENUM_BRAND_TYPE, ENUM_ORDER_PROVIDER, ENUM_STATUS } from '../../helpers/enums';
import {
  statusIsCompletedDispatchOrSearch,
  statusIsPackaging,
  statusIsReady,
  statusIsRejected,
  statusIsStarted
} from '../../helpers/permissions.service';
import { OrderType, RolePermissionType } from '../../helpers/types';
import { getDeliveryType } from '../../contexts/layout.context/utils';

const isCompleteColor = ({ status }: { status: ENUM_STATUS }) =>
  statusIsCompletedDispatchOrSearch(status);

const isRejectedColor = ({ status }: { status: ENUM_STATUS }) => statusIsRejected(status);

const isQuickEats = ({ order }: { order: OrderType }) => {
  const brand_ids = order.calculated_order?.brand_ids ?? [];
  const brand_names = brand_ids.map((brand) => brand.name.toLowerCase());
  return brand_names.some((name) => name.includes(QUICK_EATS));
};

const isBudgetBrand = ({ order }: { order: OrderType }) => {
  const brand_ids = order.calculated_order?.brand_ids ?? [];
  const brand_names = brand_ids.map((brand) => brand.brand_type);
  return brand_names.some((t) => t === ENUM_BRAND_TYPE.BUDGET);
};

const isPendingColor = ({
  order,
  isPackagingRole,
  status
}: {
  order: OrderType;
  isPackagingRole: boolean;
  status: ENUM_STATUS;
}) => {
  const role = localStorage.getItem(STORAGE_KEYS.ROLE) as ENUM_KITCHEN_ROLE;
  const firstName = localStorage.getItem(STORAGE_KEYS.FIRST_NAME);

  const adminPackaging = role.includes('ADMIN') && isPackagingRole;
  const isPackaging = statusIsPackaging(status) && firstName === 'Packaging';

  return adminPackaging || statusIsStarted(status) || isPackaging;
};

const isFrontdeskChowdeck = ({
  order,
  isFrontdeskRole
}: {
  order: OrderType;
  isFrontdeskRole: boolean;
}) => {
  return isFrontdeskRole && getDeliveryType(order) === ENUM_ORDER_PROVIDER.CHOWDECK;
};

const isFrontdeskGlovo = ({
  order,
  isFrontdeskRole
}: {
  order: OrderType;
  isFrontdeskRole: boolean;
}) => {
  return isFrontdeskRole && getDeliveryType(order) === ENUM_ORDER_PROVIDER.GLOVO;
};

const isBrandReady = ({
  order,
  isKitchenRole,
  activeBrands,
  status
}: {
  order: OrderType;
  isKitchenRole: boolean;
  activeBrands: string[];
  status: ENUM_STATUS;
}) => {
  const meals = order.calculated_order?.meals ?? [];
  const allActiveBrands = meals.map(({ brand }) => brand.id);
  const allBrandsAreActive = allActiveBrands.every((brand) => activeBrands.includes(brand));
  return isKitchenRole && statusIsStarted(status) && allBrandsAreActive;
};

const isFrontDeskUnpaidPage = () => {
  const path = location.pathname;
  return path === '/front-desk/unpaid';
};

const isScheduled = ({ order }: { order: OrderType & { isSchedulePage?: boolean } }) => {
  const role = localStorage.getItem(STORAGE_KEYS.ROLE) as ENUM_KITCHEN_ROLE;
  const isSchedulePage = order.isSchedulePage === undefined ? false : order.isSchedulePage;

  return (
    isSchedulePage ||
    (order.scheduled &&
      ([ENUM_KITCHEN_ROLE.KITCHEN_STAFF, ENUM_KITCHEN_ROLE.PACKAGING].includes(role) ||
        isFrontDeskUnpaidPage()))
  );
};

const isPreorder = ({ order }: { order: OrderType }) => {
  const role = localStorage.getItem(STORAGE_KEYS.ROLE) as ENUM_KITCHEN_ROLE;

  return (
    order.preorder &&
    ([ENUM_KITCHEN_ROLE.KITCHEN_STAFF, ENUM_KITCHEN_ROLE.PACKAGING].includes(role) ||
      isFrontDeskUnpaidPage())
  );
};

const isLoyalCustomer = ({ order }: { order: OrderType }) => {
  return order?.calculated_order?.user?.loyal_customer;
};

export const getPackagingColor = ({
  status,
  order,
  isPackagingRole
}: {
  order: OrderType;
  status: ENUM_STATUS;
  isPackagingRole: boolean;
}) => {
  const staffNumber = localStorage.getItem(STORAGE_KEYS.STAFF_NUMBER);

  const isPackaging =
    !isScheduled({ order }) &&
    !isLoyalCustomer({ order }) &&
    isPackagingRole &&
    statusIsPackaging(status) &&
    staffNumber;

  // eslint-disable-next-line
  return isPackaging ? { backgroundColor: splitScreenColors[+staffNumber - 1] } : {};
};

export const getLayoutColor = ({
  order,
  activeBrands,
  status,
  permissions
}: {
  order: OrderType;
  status: ENUM_STATUS;
  activeBrands: string[];
  permissions: RolePermissionType;
}) => {
  const { isFrontdeskRole, isPackagingRole, isKitchenRole } = permissions;

  return isQuickEats({ order })
    ? 'bg-[#FC8EAC]'
    : isBudgetBrand({ order })
    ? 'bg-[#EB6300]'
    : isScheduled({ order })
    ? 'bg-[#016FB1]'
    : isPreorder({ order })
    ? 'bg-[#511EE1]'
    : isLoyalCustomer({ order })
    ? 'bg-[#aa4993]'
    : statusIsReady(status)
    ? 'bg-[#D50000]'
    : isBrandReady({ activeBrands, isKitchenRole, order, status })
    ? 'bg-[#C1BEB9]'
    : isPendingColor({ isPackagingRole, order, status })
    ? 'bg-[#D58300]'
    : isFrontdeskChowdeck({ isFrontdeskRole, order })
    ? 'bg-[#D58300]'
    : isFrontdeskGlovo({ isFrontdeskRole, order })
    ? 'bg-[#FFC600]'
    : isCompleteColor({ status })
    ? 'bg-[#707160]'
    : isRejectedColor({ status })
    ? 'bg-white'
    : 'bg-[#46AE31]';
};
